/**
 * @fileoverview Bulk firm-contact import dialog (MOR-780)
 *
 * Two steps, mirroring the task-workflow import
 * (`src/components/settings/tasks/WorkflowImportDialog.tsx`):
 *  1. Upload a CSV / Excel list of contacts. The AI import action reads the
 *     sheet and works out which column holds which detail, so the firm never
 *     has to reformat their spreadsheet or map columns by hand.
 *  2. Review what came back — the columns the AI matched, every parsed row,
 *     and any row that already exists in the directory (flagged and
 *     deselected). The user can fix a category, deselect rows, then import.
 *
 * Rows are written by `contacts.mutations.bulkCreateFirmContacts` in batches,
 * which re-checks duplicates and custom fields server-side.
 */

import { useMemo, useRef, useState } from "react";
import { useConvex } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import { FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/ui/composed/overlays/dialog";
import { extractErrorMessage } from "@/utils/errors";
import {
  CONTACT_TYPE_META,
  CREATABLE_FIRM_CATEGORIES,
  FIRM_CATEGORY_META,
  formatAbn,
  formatAcn,
} from "./types";
import type { FirmContactCategory } from "./types";

const ACCEPT = ".csv,.xlsx,.xls";

/** Rows per `bulkCreateFirmContacts` call — matches the mutation's own cap. */
const BATCH_SIZE = 100;

type ImportResult = FunctionReturnType<
  typeof api.contacts.firmContactImport.importFirmContactsFromSpreadsheet
>;
type DraftContact = ImportResult["contacts"][number];

export function FirmContactImportDialog({
  orgId,
}: {
  orgId: Id<"organizations">;
}) {
  const convex = useConvex();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  /** Row indices the user wants to import. */
  const [selected, setSelected] = useState<Set<number>>(new Set());
  /** Per-row category corrections, keyed by row index. */
  const [categoryOverrides, setCategoryOverrides] = useState<
    Map<number, FirmContactCategory>
  >(new Map());

  const busy = parsing || importing;

  function reset() {
    setFile(null);
    setResult(null);
    setSelected(new Set());
    setCategoryOverrides(new Map());
  }

  const categoryOf = (row: DraftContact): FirmContactCategory =>
    categoryOverrides.get(row.rowIndex) ?? row.category;

  const duplicateCount = useMemo(
    () => (result?.contacts ?? []).filter((c) => c.duplicateKind).length,
    [result],
  );

  async function handleParse() {
    if (!file) return;
    setParsing(true);
    try {
      const url = await convex.mutation(
        api.contacts.mutations.generateFirmContactImportUploadUrl,
        { orgId },
      );
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }
      const { storageId } = (await response.json()) as {
        storageId: Id<"_storage">;
      };

      const parsed = await convex.action(
        api.contacts.firmContactImport.importFirmContactsFromSpreadsheet,
        { orgId, storageId, fileName: file.name },
      );

      setResult(parsed);
      // Anything that looks like a contact the firm already has starts
      // deselected, so a re-run tops up rather than duplicating.
      setSelected(
        new Set(
          parsed.contacts
            .filter((c) => !c.duplicateKind)
            .map((c) => c.rowIndex),
        ),
      );
      setCategoryOverrides(new Map());
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not read that spreadsheet."));
    } finally {
      setParsing(false);
    }
  }

  async function handleImport() {
    if (!result) return;
    const rows = result.contacts.filter((c) => selected.has(c.rowIndex));
    if (rows.length === 0) {
      toast.error("Select at least one contact to import.");
      return;
    }

    setImporting(true);
    try {
      const outcomes: { created: number; skipped: number }[] = [];
      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        const outcome = await convex.mutation(
          api.contacts.mutations.bulkCreateFirmContacts,
          {
            orgId,
            contacts: batch.map((row) => ({
              displayName: row.displayName,
              category: categoryOf(row),
              contactType: row.contactType,
              abn: row.abn ?? undefined,
              acn: row.acn ?? undefined,
              email: row.email ?? undefined,
              phone: row.phone ?? undefined,
              website: row.website ?? undefined,
              notes: row.notes ?? undefined,
              addresses: row.address ? [row.address] : undefined,
              customFields:
                Object.keys(row.customFields).length > 0
                  ? row.customFields
                  : undefined,
            })),
          },
        );
        outcomes.push({ created: outcome.created, skipped: outcome.skipped });
      }
      const created = outcomes.reduce((sum, o) => sum + o.created, 0);
      const skipped = outcomes.reduce((sum, o) => sum + o.skipped, 0);

      toast.success(
        `Imported ${created} contact${created === 1 ? "" : "s"}` +
          (skipped > 0
            ? ` — skipped ${skipped} already in the directory.`
            : "."),
      );
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not import those contacts."));
    } finally {
      setImporting(false);
    }
  }

  const toggleRow = (rowIndex: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });

  const allSelected =
    result !== null &&
    result.contacts.length > 0 &&
    selected.size === result.contacts.length;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (busy) return;
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-2">
          <Upload className="h-4 w-4" />
          Import
        </Button>
      </DialogTrigger>
      <DialogContent className={result ? "sm:max-w-5xl" : "sm:max-w-lg"}>
        <DialogHeader>
          <DialogTitle>Import contacts from a spreadsheet</DialogTitle>
          <DialogDescription className="pt-2">
            {result
              ? "Check what came through. Rows already in the directory are flagged and left out. Change a category or deselect any row you don't want."
              : "Upload a CSV or Excel list of contacts. Your columns are matched for you, so there's no need to reformat the file first."}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="flex flex-col gap-3">
            {/* What the AI matched, so the mapping is auditable. */}
            <div className="flex flex-wrap gap-1.5">
              {result.columnMatches.map((match) => (
                <Badge
                  key={`${match.field}-${match.header}`}
                  variant="soft"
                  color={match.isCustomField ? "purple" : "gray"}
                  size="sm"
                  className="font-normal"
                >
                  <span className="font-medium">{match.label}</span>
                  <span className="mx-1 opacity-60">←</span>
                  {match.header}
                </Badge>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>
                {result.contacts.length} contact
                {result.contacts.length === 1 ? "" : "s"} found
              </span>
              {duplicateCount > 0 && (
                <span>{duplicateCount} already in the directory</span>
              )}
              {result.skippedRowCount > 0 && (
                <span>
                  {result.skippedRowCount} rows without a name skipped
                </span>
              )}
            </div>

            <div className="max-h-[45vh] overflow-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allSelected}
                        aria-label="Select all contacts"
                        onCheckedChange={(checked) =>
                          setSelected(
                            checked === true
                              ? new Set(result.contacts.map((c) => c.rowIndex))
                              : new Set(),
                          )
                        }
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-[150px]">Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>ABN / ACN</TableHead>
                    <TableHead>Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.contacts.map((row) => {
                    const category = categoryOf(row);
                    const isSelected = selected.has(row.rowIndex);
                    return (
                      <TableRow
                        key={row.rowIndex}
                        className={isSelected ? undefined : "opacity-50"}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            aria-label={`Import ${row.displayName}`}
                            onCheckedChange={() => toggleRow(row.rowIndex)}
                          />
                        </TableCell>
                        <TableCell className="max-w-[220px]">
                          <p className="truncate text-sm font-medium">
                            {row.displayName}
                          </p>
                          {row.duplicateKind && (
                            <Badge
                              variant="soft"
                              color="amber"
                              size="sm"
                              className="mt-1 font-normal"
                            >
                              {row.duplicateKind === "existing"
                                ? `Already in directory${
                                    row.duplicateOf
                                      ? ` — ${row.duplicateOf}`
                                      : ""
                                  }`
                                : "Repeated row"}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={category}
                            onValueChange={(value) =>
                              setCategoryOverrides((prev) => {
                                const next = new Map(prev);
                                next.set(
                                  row.rowIndex,
                                  value as FirmContactCategory,
                                );
                                return next;
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CREATABLE_FIRM_CATEGORIES.map((c) => {
                                const meta = FIRM_CATEGORY_META[c];
                                return (
                                  <SelectItem key={c} value={c}>
                                    <span className="inline-flex items-center gap-2">
                                      <meta.icon className="h-3.5 w-3.5" />
                                      {meta.label}
                                    </span>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {CONTACT_TYPE_META[row.contactType].label}
                        </TableCell>
                        <TableCell className="max-w-[180px] truncate text-xs text-muted-foreground">
                          {row.email ?? "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {row.phone ?? "—"}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {row.abn
                            ? formatAbn(row.abn)
                            : row.acn
                              ? formatAcn(row.acn)
                              : "—"}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">
                          {formatAddress(row.address) ?? "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : (
          <div className="py-2">
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="flex w-full items-center gap-3 rounded-lg border border-dashed border-border px-4 py-5 text-left transition-colors hover:bg-muted/50 disabled:opacity-60"
            >
              {file ? (
                <FileSpreadsheet className="size-6 shrink-0 text-primary" />
              ) : (
                <Upload className="size-6 shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-primary">
                  {file ? file.name : "Choose a file"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {file
                    ? "Click to choose a different file"
                    : "CSV or Excel (.csv, .xlsx)"}
                </p>
              </div>
            </button>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <>
              <Button variant="ghost" onClick={reset} disabled={busy}>
                Choose another file
              </Button>
              <Button
                onClick={() => void handleImport()}
                disabled={busy || selected.size === 0}
              >
                {importing ? (
                  <>
                    <Loader2 className="mr-1.5 size-4 animate-spin" />
                    Importing…
                  </>
                ) : (
                  `Import ${selected.size} contact${selected.size === 1 ? "" : "s"}`
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                onClick={() => void handleParse()}
                disabled={!file || busy}
              >
                {parsing ? (
                  <>
                    <Loader2 className="mr-1.5 size-4 animate-spin" />
                    Importing…
                  </>
                ) : (
                  "Import"
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One-line address preview for the review table. */
function formatAddress(address: DraftContact["address"]): string | null {
  if (!address) return null;
  const parts = [
    address.line1,
    address.line2,
    address.suburb,
    [address.state, address.postcode].filter(Boolean).join(" "),
  ].filter((p): p is string => Boolean(p && p.trim()));
  return parts.length > 0 ? parts.join(", ") : null;
}
